import { adminAPI } from './api';

export const financialService = {
  // Get financial overview
  getFinancialOverview: async (timeframe = 'month') => {
    try {
      console.log('💰 Fetching financial overview for timeframe:', timeframe);
      const response = await adminAPI.getFinancialOverview(timeframe);
      
      console.log('📊 Financial overview response:', response.data);
      
      return {
        success: true,
        data: response.data.data || {}
      };
    } catch (error) {
      console.error('❌ Failed to fetch financial overview:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load financial overview'
      };
    }
  },

  // Get payment withdrawal requests
  getWithdrawalRequests: async (status = 'pending', page = 1, limit = 20) => {
    try {
      console.log('💳 Fetching withdrawal requests:', { status, page, limit });
      const response = await adminAPI.getWithdrawalRequests(status, page, limit);
      
      console.log('📋 Withdrawal requests response:', response.data);
      
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination || {}
      };
    } catch (error) {
      console.error('❌ Failed to fetch withdrawal requests:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load withdrawal requests'
      };
    }
  },

  // Approve withdrawal request
  approveWithdrawal: async (requestId, adminNotes = '') => {
    try {
      console.log('✅ Approving withdrawal request:', requestId);
      const response = await adminAPI.approveWithdrawal(requestId, { adminNotes });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Failed to approve withdrawal:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to approve withdrawal'
      };
    }
  },

  // Reject withdrawal request
  rejectWithdrawal: async (requestId, reason) => {
    try {
      console.log('❌ Rejecting withdrawal request:', requestId);
      const response = await adminAPI.rejectWithdrawal(requestId, { reason });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Failed to reject withdrawal:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to reject withdrawal'
      };
    }
  },

  // Get commission reports
  getCommissionReports: async (startDate, endDate, page = 1, limit = 20) => {
    try {
      console.log('📈 Fetching commission reports:', { startDate, endDate });
      const response = await adminAPI.getCommissionReports(startDate, endDate, page, limit);
      
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination || {}
      };
    } catch (error) {
      console.error('❌ Failed to fetch commission reports:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load commission reports'
      };
    }
  },

  // Generate financial report
  generateReport: async (reportType, startDate, endDate) => {
    try {
      console.log('📄 Generating report:', { reportType, startDate, endDate });
      const response = await adminAPI.generateReport(reportType, startDate, endDate);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Failed to generate report:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to generate report'
      };
    }
  },

  // Payment Gateway Integration
  initiatePayout: async (requestId, paymentMethod, paymentDetails) => {
    try {
      console.log(`💸 Initiating ${paymentMethod} payout for request:`, requestId);
      const response = await adminAPI.post(`/payments/${paymentMethod}/payout`, {
        requestId,
        ...paymentDetails
      });
      
      return {
        success: true,
        data: response.data,
        transactionId: response.data.transaction_id
      };
    } catch (error) {
      console.error(`❌ ${paymentMethod} payout failed:`, error);
      return {
        success: false,
        message: error.response?.data?.message || `${paymentMethod} payout failed`
      };
    }
  },

  // Get payment methods available for a tutor
  getTutorPaymentMethods: async (tutorId) => {
    try {
      const response = await adminAPI.get(`/payments/tutors/${tutorId}/methods`);
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('❌ Failed to fetch payment methods:', error);
      return {
        success: false,
        message: 'Failed to load payment methods'
      };
    }
  },

  // Verify payment gateway configuration
  verifyPaymentGateway: async (gateway) => {
    try {
      const response = await adminAPI.get(`/payments/${gateway}/verify`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        message: `${gateway} configuration issue`
      };
    }
  }
};

// Payment Gateway Specific Services
export const paymentService = {
  // PayPal Payout Integration
  paypal: {
    initiatePayout: async (email, amount, currency = 'USD', note = 'Tutor payout') => {
      try {
        const response = await adminAPI.post('/payments/paypal/payout', {
          recipient_email: email,
          amount: {
            value: amount,
            currency: currency
          },
          note: note,
          sender_item_id: `payout_${Date.now()}`,
          recipient_wallet: 'PAYPAL'
        });

        return {
          success: true,
          data: response.data,
          payout_batch_id: response.data.batch_id,
          transaction_id: response.data.transaction_id
        };
      } catch (error) {
        console.error('❌ PayPal payout error:', error);
        return {
          success: false,
          message: error.response?.data?.message || 'PayPal payout failed. Please check recipient email and balance.'
        };
      }
    },

    // Check payout status
    checkPayoutStatus: async (payoutBatchId) => {
      try {
        const response = await adminAPI.get(`/payments/paypal/payouts/${payoutBatchId}`);
        return {
          success: true,
          data: response.data
        };
      } catch (error) {
        return {
          success: false,
          message: 'Failed to check payout status'
        };
      }
    }
  },

  // PayStack Transfer Integration
  paystack: {
    initiateTransfer: async (recipientCode, amount, currency = 'NGN', reason = 'Tutor payout') => {
      try {
        // PayStack amounts are in kobo (1 NGN = 100 kobo)
        const amountInKobo = Math.round(amount * 100);
        
        const response = await adminAPI.post('/payments/paystack/transfer', {
          recipient: recipientCode,
          amount: amountInKobo,
          currency: currency,
          reason: reason,
          reference: `transfer_${Date.now()}`
        });

        return {
          success: true,
          data: response.data,
          transfer_code: response.data.transfer_code,
          transaction_id: response.data.id
        };
      } catch (error) {
        console.error('❌ PayStack transfer error:', error);
        return {
          success: false,
          message: error.response?.data?.message || 'PayStack transfer failed. Please check recipient code and balance.'
        };
      }
    },

    // Create transfer recipient (if needed)
    createRecipient: async (accountNumber, bankCode, accountName) => {
      try {
        const response = await adminAPI.post('/payments/paystack/recipient', {
          type: 'nuban',
          name: accountName,
          account_number: accountNumber,
          bank_code: bankCode,
          currency: 'NGN'
        });

        return {
          success: true,
          data: response.data,
          recipient_code: response.data.recipient_code
        };
      } catch (error) {
        return {
          success: false,
          message: 'Failed to create transfer recipient'
        };
      }
    },

    // Check transfer status
    checkTransferStatus: async (transferCode) => {
      try {
        const response = await adminAPI.get(`/payments/paystack/transfers/${transferCode}`);
        return {
          success: true,
          data: response.data
        };
      } catch (error) {
        return {
          success: false,
          message: 'Failed to check transfer status'
        };
      }
    }
  }
};